/* eslint-disable react/no-array-index-key */
import React, { useState } from 'react';
import { X, Check, Star, Trophy } from 'lucide-react';
import axios from '../../utils/api';
import { API } from '../../utils/api';
import { resolvePollImg } from '../polls/PollOptionsEditor';
import PhotoViewer from '../shared/PhotoViewer';

const TEAL = '#4DB6AC';

/**
 * Org-side poll modal, wired to the org `admin_events` endpoints.
 * poll: { eventId, title, options: [{id, text, image_url?, vote_count?}], my_vote, is_closed, winner_ids, star_reward? }
 * vote_count is only sent by the server when results may be shown (closed poll, or HR).
 */
const ResultLine = ({ opt, total, isWinner }) => {
  const pct = total > 0 ? Math.round(((opt.vote_count || 0) / total) * 100) : 0;
  return (
    <div data-testid={`org-poll-result-${opt.id}`} style={{ marginTop: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#4B5563', marginBottom: 3 }}>
        <span>{opt.vote_count || 0} vote{opt.vote_count === 1 ? '' : 's'}</span><span>{pct}%</span>
      </div>
      <div style={{ height: 6, background: '#F3F4F6', borderRadius: 3 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: isWinner ? '#F59E0B' : TEAL, borderRadius: 3 }} />
      </div>
    </div>
  );
};

export default function OrgPollModal({ poll, currentUserId, onClose, onVoted }) {
  const [myVote, setMyVote] = useState(poll?.my_vote || null);
  const [selected, setSelected] = useState(poll?.my_vote || null);
  const [submitting, setSubmitting] = useState(false);
  const [justVoted, setJustVoted] = useState(false);
  const [err, setErr] = useState('');
  const [viewer, setViewer] = useState(null);

  if (!poll) return null;

  const options = poll.options || [];
  const closed = !!poll.is_closed;
  const isPhoto = options.some(o => o.image_url);
  const showCounts = options.some(o => typeof o.vote_count === 'number');
  const total = options.reduce((s, o) => s + (o.vote_count || 0), 0);
  const winners = new Set(poll.winner_ids || []);
  const starReward = poll.star_reward;
  const canSubmit = !closed && selected && selected !== myVote;

  const submit = async () => {
    if (!selected) { setErr('Pick an option first.'); return; }
    setSubmitting(true);
    setErr('');
    try {
      await axios.post(`${API}/admin-events/${poll.eventId}/vote/${selected}`);
      setMyVote(selected);
      setJustVoted(true);
      if (onVoted) onVoted(poll.eventId, selected);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not submit. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const pick = (id) => { if (!closed) { setSelected(id); setJustVoted(false); } };

  const renderPhotoCard = (opt) => {
    const picked = selected === opt.id;
    const win = closed && winners.has(opt.id);
    return (
      <div key={opt.id} data-testid={`org-poll-option-${opt.id}`}
        style={{ border: `2px solid ${win ? '#F59E0B' : picked ? TEAL : '#E5E7EB'}`, borderRadius: 12, overflow: 'hidden', background: win ? '#FFFBEB' : '#FFF' }}>
        {opt.image_url ? (
          <button type="button" onClick={() => setViewer({ src: resolvePollImg(opt.image_url), alt: opt.text })} aria-label={`View ${opt.text} full size`}
            data-testid={`org-poll-photo-${opt.id}`} style={{ display: 'block', width: '100%', padding: 0, border: 'none', cursor: 'zoom-in', background: '#F3F4F6' }}>
            <img src={resolvePollImg(opt.image_url)} alt={opt.text} loading="lazy" decoding="async"
              style={{ display: 'block', width: '100%', aspectRatio: '4 / 3', objectFit: 'cover' }} />
          </button>
        ) : (
          <div style={{ width: '100%', aspectRatio: '4 / 3', background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', fontSize: 12 }}>No photo</div>
        )}
        <div style={{ padding: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, color: '#1F2937', overflowWrap: 'anywhere' }}>
            {win && <Trophy size={15} color="#F59E0B" style={{ flexShrink: 0 }} />}{opt.text}
          </div>
          {showCounts && <ResultLine opt={opt} total={total} isWinner={win} />}
          {!closed && (
            <button type="button" onClick={() => pick(opt.id)} data-testid={`org-poll-select-${opt.id}`} aria-pressed={picked}
              style={{ marginTop: 8, width: '100%', padding: '9px 10px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                border: `1.5px solid ${picked ? TEAL : '#D1D5DB'}`, background: picked ? TEAL : '#FFF', color: picked ? '#FFF' : '#374151',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              {picked && <Check size={15} />}{picked ? 'Selected' : 'Vote'}
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderTextOption = (opt) => {
    const picked = selected === opt.id;
    const win = closed && winners.has(opt.id);
    return (
      <button key={opt.id} type="button" onClick={() => pick(opt.id)} disabled={closed}
        data-testid={`org-poll-option-${opt.id}`}
        style={{ textAlign: 'left', padding: '12px 14px', borderRadius: 10, border: `1.5px solid ${win ? '#F59E0B' : picked ? TEAL : '#E5E7EB'}`,
          background: win ? '#FFFBEB' : picked ? `${TEAL}15` : '#FFF', fontSize: 14, color: '#1F2937', cursor: closed ? 'default' : 'pointer',
          fontWeight: picked || win ? 600 : 400 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{win && <Trophy size={15} color="#F59E0B" />}{opt.text}</span>
        {showCounts && <ResultLine opt={opt} total={total} isWinner={win} />}
      </button>
    );
  };

  const myVoteText = options.find(o => o.id === myVote)?.text;

  return (
    <div onClick={onClose} data-testid="org-poll-modal"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: '#FFF', borderRadius: 16, padding: 20, width: '100%', maxWidth: isPhoto ? 720 : 420, maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, padding: '4px 10px', borderRadius: 12, background: 'rgba(245,158,11,0.14)', color: '#B45309' }}>
            Poll
          </span>
          {closed && (
            <span data-testid="org-poll-closed-label" style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 12, background: '#F3F4F6', color: '#374151' }}>Voting closed</span>
          )}
          {starReward > 0 && (
            <span data-testid="org-poll-modal-star-badge"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: '#92400E', background: '#FEF3C7', padding: '4px 8px', borderRadius: 10 }}>
              <Star size={12} fill="#F59E0B" color="#F59E0B" />+{starReward}
            </span>
          )}
          <button onClick={onClose} data-testid="org-poll-modal-close" aria-label="Close"
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        <h2 data-testid="org-poll-modal-question" style={{ fontSize: 18, fontWeight: 700, color: '#1F2937', margin: '0 0 6px', lineHeight: 1.4 }}>{poll.title}</h2>
        <p data-testid="org-poll-status-text" style={{ fontSize: 13, color: '#6B7280', margin: '0 0 14px' }}>
          {closed
            ? (total > 0 ? `Final results · ${total} vote${total === 1 ? '' : 's'}` : 'No votes were cast.')
            : showCounts ? `HR view · ${total} vote${total === 1 ? '' : 's'} so far. Employees see results when voting closes.`
              : 'Results will show when voting closes.'}
        </p>

        <div style={isPhoto
          ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))', gap: 12, marginBottom: 12 }
          : { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
          {options.map(opt => (isPhoto ? renderPhotoCard(opt) : renderTextOption(opt)))}
        </div>

        {myVote && !closed && (
          <div data-testid="org-poll-result-banner"
            style={{ padding: '10px 12px', borderRadius: 10, marginBottom: 12, background: `${TEAL}15`, border: `1px solid ${TEAL}`, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <Check size={16} color={TEAL} />
            <span style={{ color: '#155E59', fontWeight: 600 }}>
              {justVoted && starReward > 0 ? `Vote recorded. +${starReward} stars credited. ` : justVoted ? 'Vote recorded. ' : ''}
              Your vote: {myVoteText}. You can change it until voting closes.
            </span>
          </div>
        )}
        {myVote && closed && (
          <p data-testid="org-poll-my-vote" style={{ fontSize: 13, color: '#4B5563', margin: '0 0 12px' }}>You voted for {myVoteText}.</p>
        )}

        {err && (
          <div style={{ padding: '8px 12px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, fontSize: 13, color: '#B91C1C', marginBottom: 12 }}>{err}</div>
        )}

        {!closed && (
          <button onClick={submit} disabled={submitting || !canSubmit} data-testid="org-poll-submit-btn"
            style={{ position: isPhoto ? 'sticky' : 'static', bottom: 0, width: '100%', padding: 12, background: submitting || !canSubmit ? '#9CA3AF' : TEAL,
              color: '#FFF', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: submitting || !canSubmit ? 'not-allowed' : 'pointer' }}>
            {submitting ? 'Submitting…' : myVote ? (selected === myVote ? 'Vote submitted' : 'Change my vote') : 'Submit vote'}
          </button>
        )}
      </div>
      {viewer && <PhotoViewer src={viewer.src} alt={viewer.alt} onClose={() => setViewer(null)} />}
    </div>
  );
}
